import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, Target, MessageCircle, Clock, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const reasonIcons = [Shield, Target, Award, Clock, MessageCircle];

const WhyChooseUs = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { t } = useLanguage();

  const reasons = [
    { icon: reasonIcons[0], title: t('whyUs.reason1.title'), subtitle: t('whyUs.reason1.subtitle'), description: t('whyUs.reason1.desc') },
    { icon: reasonIcons[1], title: t('whyUs.reason2.title'), subtitle: t('whyUs.reason2.subtitle'), description: t('whyUs.reason2.desc') },
    { icon: reasonIcons[2], title: t('whyUs.reason3.title'), subtitle: t('whyUs.reason3.subtitle'), description: t('whyUs.reason3.desc') },
    { icon: reasonIcons[3], title: t('whyUs.reason4.title'), subtitle: t('whyUs.reason4.subtitle'), description: t('whyUs.reason4.desc') },
    { icon: reasonIcons[4], title: t('whyUs.reason5.title'), subtitle: t('whyUs.reason5.subtitle'), description: t('whyUs.reason5.desc') },
  ];

  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#EFECE5] to-[#F8F6F1] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      {/* Decorative gold corners */}
      <div className="absolute top-12 left-12 w-16 h-16 border-l-2 border-t-2 border-[#C5A55C]/40" />
      <div className="absolute top-12 right-12 w-16 h-16 border-r-2 border-t-2 border-[#C5A55C]/40" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="text-center mb-24"
        >
          <motion.span 
            className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {t('whyUs.tagline')}
          </motion.span>
          <h2 className="font-montserrat font-light text-4xl md:text-6xl text-[#1A1A1A] tracking-[0.1em] uppercase">
            {t('whyUs.title')}
          </h2>
          <div className="mt-6 w-24 h-[2px] bg-[#C5A55C] mx-auto" />
        </motion.div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {reasons.map((reason, index) => (
            <ReasonCard
              key={index}
              reason={reason}
              inView={inView}
              delay={index * 0.1}
            />
          ))}
        </div>
      </div>
      
      {/* Decorative gold corners bottom */}
      <div className="absolute bottom-12 left-12 w-16 h-16 border-l-2 border-b-2 border-[#C5A55C]/40" />
      <div className="absolute bottom-12 right-12 w-16 h-16 border-r-2 border-b-2 border-[#C5A55C]/40" />
    </section>
  );
};

const ReasonCard = ({ reason, inView, delay }) => {
  const Icon = reason.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.8, ease: 'easeOut' }}
      className="group p-8 bg-white shadow-lg hover:shadow-xl transition-all duration-500 text-center relative overflow-hidden border border-[#C5A55C]/10 hover:border-[#C5A55C]/40"
    >
      {/* Shimmer effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-[#C5A55C]/10 to-transparent" />
      </div>
      
      {/* Icon */}
      <div className="mb-6 flex justify-center relative z-10">
        <div className="w-16 h-16 bg-[#C5A55C]/10 group-hover:bg-[#C5A55C] flex items-center justify-center transition-all duration-300 rounded-sm">
          <Icon className="w-7 h-7 text-[#C5A55C] group-hover:text-white stroke-[1.5] transition-colors duration-300" />
        </div>
      </div>
      
      {/* Content */}
      <span className="font-montserrat font-medium text-[9px] tracking-[0.25em] text-[#C5A55C] uppercase block mb-2 relative z-10">
        {reason.subtitle}
      </span>
      <h3 className="font-montserrat font-medium text-lg text-[#1A1A1A] tracking-wide mb-3 relative z-10">
        {reason.title}
      </h3>
      <p className="font-raleway font-normal text-sm text-[#1A1A1A]/60 leading-relaxed relative z-10">
        {reason.description}
      </p>
      
      {/* Gold accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5A55C] scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
    </motion.div>
  );
};

export default WhyChooseUs;
